import { Injectable, signal } from '@angular/core';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { toArucoPicture } from '@modules/aruco/adapters/aruco-picture.adapter';
import { ArucoPicture } from '@modules/aruco/models/aruco-picture';
import { MediaListItem } from '@modules/shared/media-picker/models/media-list-item';
import { MediaPickerValidator } from '@modules/shared/media-picker/models/media-picker-validator';
import { getFileKey, getMediaKey, getMediaListItemKey } from '@modules/shared/media-picker/utils/media-picker-key';
import { Media, StickerDetectionType } from '@pixacare/domain';

@Injectable()
export class PictureProviderService {
  private readonly picturesByKey = signal<Map<string, ArucoPicture>>(new Map());

  init(inputs: readonly (Media | File)[]): void {
    const map = new Map<string, ArucoPicture>();
    for (const input of inputs) {
      const picture = toArucoPicture(input);
      const key = input instanceof File ? getFileKey(input) : getMediaKey(input.id);
      map.set(key, picture);
    }
    this.picturesByKey.set(map);
  }

  get(item: MediaListItem): ArucoPicture | undefined {
    return this.picturesByKey().get(getMediaListItemKey(item));
  }

  // Saved medias: selectability comes from the persisted sticker status (no detection).
  // Files: selectability comes from the live detection emitted by the file tile.
  readonly isItemSelectable = (item: MediaListItem): boolean =>
    item.type === 'media'
      ? item.media.stickerDetectionType === StickerDetectionType.CALIBRATION_V1
      : !!this.get(item)?.arucoDetected;

  // Only files carry a surface at selection time; saved medias resolve it on confirm.
  readonly validators: MediaPickerValidator[] = [
    ({ files }): string | null => {
      const map = this.picturesByKey();
      const picks = files
        .map((file) => map.get(getFileKey(file)))
        .filter((picture): picture is ArucoPicture => !!picture);
      if (picks.length === 0) {
        return null;
      }
      return picks.some((picture) => picture.arucoDetected === false) ? marker('Pas de sticker détecté') : null;
    },
  ];

  registerUploadedFiles(files: readonly File[]): void {
    this.picturesByKey.update((current) => {
      const next = new Map(current);
      for (const file of files) {
        const key = getFileKey(file);
        if (!next.has(key)) {
          next.set(key, toArucoPicture(file));
        }
      }
      return next;
    });
  }

  setDetected(item: MediaListItem, detected: boolean): void {
    this.patch(getMediaListItemKey(item), (picture) => ({ ...picture, arucoDetected: detected }));
  }

  setSurface(item: MediaListItem, surface: number): void {
    this.patch(getMediaListItemKey(item), (picture) => ({ ...picture, arucoSurface: surface }));
  }

  private patch(key: string, update: (picture: ArucoPicture) => ArucoPicture): void {
    this.picturesByKey.update((current) => {
      const existing = current.get(key);
      if (!existing) {
        return current;
      }
      const next = new Map(current);
      next.set(key, update(existing));
      return next;
    });
  }
}
